import { WidgetDefinition } from './types';
import { widgetRegistry } from './registry';

// Universal widgets
import { NotificationsWidget } from '@/components/dashboard/widgets/NotificationsWidget';
import { SupportTicketsWidget } from '@/components/dashboard/widgets/SupportTicketsWidget';
import { QuickActionsWidget } from '@/components/dashboard/widgets/QuickActionsWidget';
import { RecentActivitiesWidget } from '@/components/dashboard/widgets/RecentActivitiesWidget';
import { CalendarWidget } from '@/components/dashboard/widgets/CalendarWidget';
import { SystemPerformanceWidget } from '@/components/dashboard/widgets/SystemPerformanceWidget';

// Fleet widgets
import { 
  FleetActiveVehiclesWidget, 
  FleetMaintenanceWidget, 
  FleetAlertsWidget, 
  FleetFuelWidget 
} from '@/components/dashboard/widgets/FleetStatsWidget';
import { FleetDriversWidget, FleetTripsWidget } from '@/components/dashboard/widgets/FleetDriversWidget';
import { FleetQuickActionsWidget } from '@/components/dashboard/widgets/FleetQuickActionsWidget';
import { FleetMapWidget } from '@/components/dashboard/widgets/FleetMapWidget';
import { MaintenanceCalendarWidget } from '@/components/dashboard/widgets/MaintenanceCalendarWidget';

// Tracking widgets
import {
  TrackingOnlineVehiclesWidget,
  TrackingTripsWidget,
  TrackingGeofenceAlertsWidget,
  TrackingSpeedAlertsWidget
} from '@/components/dashboard/widgets/TrackingStatsWidget';
import { LiveRouteWidget } from '@/components/dashboard/widgets/LiveRouteWidget';

// Usage widgets
import { usageWidgetDefinitions } from './usage-widgets';

// ProductionONE removed - system discontinued

const widgetDefinitions: WidgetDefinition[] = [
  // Universal widgets
  {
    type: 'notifications',
    title: 'Notificações',
    description: 'Últimas notificações do sistema',
    icon: 'Bell',
    defaultSize: { w: 2, h: 2 },
    component: NotificationsWidget
  },
  {
    type: 'support-tickets',
    title: 'Chamados de Suporte',
    description: 'Chamados recentes e pendentes',
    icon: 'Headphones',
    defaultSize: { w: 2, h: 2 },
    component: SupportTicketsWidget
  },
  {
    type: 'quick-actions',
    title: 'Ações Rápidas',
    description: 'Botões para ações frequentes',
    icon: 'Zap',
    defaultSize: { w: 1, h: 2 },
    component: QuickActionsWidget
  },
  {
    type: 'recent-activities',
    title: 'Atividades Recentes',
    description: 'Timeline de atividades da empresa',
    icon: 'Clock',
    defaultSize: { w: 2, h: 2 },
    component: RecentActivitiesWidget
  },
  {
    type: 'calendar',
    title: 'Próximos Eventos',
    description: 'Agenda e compromissos próximos',
    icon: 'Calendar',
    defaultSize: { w: 2, h: 2 },
    component: CalendarWidget
  },
  {
    type: 'system-performance',
    title: 'Performance do Sistema',
    description: 'Monitoramento em tempo real',
    icon: 'Activity',
    defaultSize: { w: 2, h: 2 },
    component: SystemPerformanceWidget
  },

  // FleetONE widgets
  {
    type: 'fleet-active-vehicles',
    title: 'Veículos Ativos',
    description: 'Total de veículos operacionais',
    icon: 'Truck',
    systemCode: 'fleet',
    defaultSize: { w: 1, h: 1 },
    component: FleetActiveVehiclesWidget
  },
  {
    type: 'fleet-maintenance',
    title: 'Manutenções Pendentes',
    description: 'Manutenções aguardando execução',
    icon: 'Wrench',
    systemCode: 'fleet',
    defaultSize: { w: 1, h: 1 },
    component: FleetMaintenanceWidget
  },
  {
    type: 'fleet-alerts',
    title: 'Alertas da Frota',
    description: 'Alertas ativos que requerem atenção',
    icon: 'AlertTriangle',
    systemCode: 'fleet',
    defaultSize: { w: 1, h: 1 },
    component: FleetAlertsWidget
  },
  {
    type: 'fleet-fuel',
    title: 'Combustível Mensal',
    description: 'Gastos com combustível',
    icon: 'Fuel',
    systemCode: 'fleet',
    defaultSize: { w: 1, h: 1 },
    component: FleetFuelWidget
  },
  {
    type: 'fleet-drivers',
    title: 'Motoristas',
    description: 'Motoristas ativos na empresa',
    icon: 'User',
    systemCode: 'fleet',
    defaultSize: { w: 1, h: 1 },
    component: FleetDriversWidget
  },
  {
    type: 'fleet-trips',
    title: 'Viagens',
    description: 'Viagens realizadas no mês',
    icon: 'Route',
    systemCode: 'fleet',
    defaultSize: { w: 1, h: 1 },
    component: FleetTripsWidget
  },
  {
    type: 'fleet-quick-actions',
    title: 'Ações Rápidas Fleet',
    description: 'Acesso rápido às funcionalidades do FleetONE',
    icon: 'Plus',
    systemCode: 'fleet',
    defaultSize: { w: 2, h: 2 },
    component: FleetQuickActionsWidget
  },
  {
    type: 'fleet-map',
    title: 'Mapa da Frota',
    description: 'Localização dos veículos em tempo real',
    icon: 'MapPin',
    systemCode: 'fleet',
    defaultSize: { w: 2, h: 3 },
    component: FleetMapWidget
  },
  {
    type: 'maintenance-calendar',
    title: 'Calendário de Manutenção',
    description: 'Manutenções programadas e pendentes',
    icon: 'CalendarDays',
    systemCode: 'fleet',
    defaultSize: { w: 2, h: 3 },
    component: MaintenanceCalendarWidget
  },

  // TrackingONE widgets
  {
    type: 'tracking-online-vehicles',
    title: 'Veículos Online',
    description: 'Veículos transmitindo posição',
    icon: 'Navigation',
    systemCode: 'gps_tracking',
    defaultSize: { w: 1, h: 1 },
    component: TrackingOnlineVehiclesWidget
  },
  {
    type: 'tracking-trips',
    title: 'Viagens Hoje',
    description: 'Viagens realizadas hoje',
    icon: 'Route',
    systemCode: 'gps_tracking',
    defaultSize: { w: 1, h: 1 },
    component: TrackingTripsWidget
  },
  {
    type: 'tracking-geofence-alerts',
    title: 'Alertas de Geocerca',
    description: 'Alertas de geocerca do dia',
    icon: 'MapPin',
    systemCode: 'gps_tracking',
    defaultSize: { w: 1, h: 1 },
    component: TrackingGeofenceAlertsWidget
  },
  {
    type: 'tracking-speed-alerts',
    title: 'Alertas de Velocidade',
    description: 'Alertas de velocidade da semana',
    icon: 'Gauge',
    systemCode: 'gps_tracking',
    defaultSize: { w: 1, h: 1 },
    component: TrackingSpeedAlertsWidget
  },
  {
    type: 'live-route',
    title: 'Rota Ao Vivo',
    description: 'Acompanhamento de rota em tempo real',
    icon: 'Navigation2',
    systemCode: 'gps_tracking',
    defaultSize: { w: 2, h: 3 },
    component: LiveRouteWidget
  },

  // ProductionONE removed - system discontinued
];

// Register all widgets
export function registerAllWidgets() {
  widgetDefinitions.forEach(widget => {
    widgetRegistry.register(widget);
  });
  
  // Register usage widgets
  usageWidgetDefinitions.forEach(widget => {
    widgetRegistry.register(widget);
  });
}

export { widgetDefinitions };
